import { boxFromCorners, snapAngle, squareCorner } from './markupConstraints.ts';
import { appendPenPoint, distance } from './markupGeometry.ts';
import type { MarkupAction } from './markupHistory.ts';
import type { DrawTool, MarkupShape, Point, TextShape } from './markupShapes.ts';

export interface StrokeStyle {
    color: string;
    width: number;
}

// The raw pointer is kept, so pressing or releasing Shift mid-drag re-shapes the mark.
export interface StrokeDraft {
    tool: DrawTool;
    id: string;
    style: StrokeStyle;
    anchor: Point;
    current: Point;
    points: readonly Point[];
    constrain: boolean;
}

export interface TextDraft {
    id: string;
    color: string;
    fontSize: number;
    at: Point;
    value: string;
    // True when re-editing an existing label rather than placing a new one.
    editing: boolean;
}

// In screen pixels: below these a pen sample is noise and a shape is a stray click.
export const MIN_PEN_STEP_SCREEN_PX = 1.5;
export const MIN_SHAPE_SCREEN_PX = 6;

export function startStroke(
    tool: DrawTool,
    id: string,
    style: StrokeStyle,
    at: Point
): StrokeDraft {
    return { tool, id, style, anchor: at, current: at, points: [at], constrain: false };
}

export function extendStroke(
    draft: StrokeDraft,
    points: readonly Point[],
    scale: number
): StrokeDraft {
    const last = points[points.length - 1];
    if (!last) return draft;
    if (draft.tool !== 'pen') return { ...draft, current: last };
    const minStep = scale > 0 ? MIN_PEN_STEP_SCREEN_PX / scale : 0;
    const extended = points.reduce(
        (acc: readonly Point[], point) => appendPenPoint(acc, point, minStep),
        draft.points
    );
    return { ...draft, current: last, points: extended };
}

export function constrainStroke(draft: StrokeDraft, constrain: boolean): StrokeDraft {
    return draft.constrain === constrain ? draft : { ...draft, constrain };
}

export function draftShape(draft: StrokeDraft): MarkupShape {
    const { tool, id, style, anchor, current, constrain } = draft;
    switch (tool) {
        case 'pen':
            return { kind: 'pen', id, ...style, points: draft.points };
        case 'arrow':
        case 'line':
            return {
                kind: tool,
                id,
                ...style,
                from: anchor,
                to: constrain ? snapAngle(anchor, current) : current
            };
        case 'rect':
        case 'ellipse': {
            const corner = constrain ? squareCorner(anchor, current) : current;
            return { kind: tool, id, ...style, box: boxFromCorners(anchor, corner) };
        }
    }
}

function screenExtent(shape: MarkupShape, scale: number): number {
    if (shape.kind === 'arrow' || shape.kind === 'line')
        return distance(shape.from, shape.to) * scale;
    if (shape.kind === 'rect' || shape.kind === 'ellipse') {
        return Math.max(shape.box.width, shape.box.height) * scale;
    }
    return Infinity;
}

export function finishStroke(draft: StrokeDraft, scale: number): MarkupShape | null {
    const shape = draftShape(draft);
    return screenExtent(shape, scale) >= MIN_SHAPE_SCREEN_PX ? shape : null;
}

// Whitespace-only text is a cancelled label, not an empty mark.
export function finishText(draft: TextDraft): TextShape | null {
    const text = draft.value.trim();
    if (text === '') return null;
    return {
        kind: 'text',
        id: draft.id,
        color: draft.color,
        fontSize: draft.fontSize,
        at: draft.at,
        text
    };
}

export function textCommitAction(
    draft: TextDraft,
    original: TextShape | null
): MarkupAction | null {
    const shape = finishText(draft);
    if (!draft.editing || !original) return shape ? { type: 'add', shape } : null;
    if (!shape) return { type: 'remove', id: original.id };
    if (shape.text === original.text) return null;
    return { type: 'update', shape: { ...original, text: shape.text } };
}
